import { getCoverageLineConfig, getCoverageLinesConfig } from '@common/config';
import { convertCoverageLinesTextToEnum, getIsHeraldSubmission } from '@common/utils';
import { Transition } from 'history';
import { isEmpty } from 'lodash';
import { useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { FormProvider } from 'react-hook-form';
import { Box, Button, Stack } from '@common-components';
import { CoverageLine, SubmissionStatus, SubmissionTask, SubmissionTaskStatus } from 'enums';
import { ExtractedDataFields, useFormProvider, useMutateSubmission } from 'hooks';
import { messages } from 'i18n';
import { PartialSubmission } from 'types';
import { FormMultiSelectAutocomplete } from 'components/hookFormComponents/FormMultiSelectAutocomplete';
import { BROKER_NESTED_ROUTES } from 'broker/broker-routes';
import Banner, { BannerMode } from 'broker/components/Banner';
import InfoBanner from 'broker/components/common/InfoBanner';
import { DirtyRouteGuard } from 'broker/components/DirtyContentGuard';
import { AIReason } from 'broker/pages/SubmissionWorkspacePage/components/AIGeneratedContent/AIReason';
import InnerPageLayout from 'broker/pages/SubmissionWorkspacePage/components/InnerPage/InnerPageLayout';
import ApiProductsDropDown from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/EditSubmissionNew/components/ApiProductsDropDown';
import { useIsAcknowledgmentFlow, useIsBORFlow } from 'broker/pages/SubmissionWorkspacePage/hooks';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';
import { PostSubmissionCoverageLineUpdateProps } from 'broker/pages/SubmissionWorkspacePage/utils/types';
import { useSendPromptReport } from 'broker/utils/use-send-prompt-report';
import { extractValidCoverageLinesSuggestion } from './extracted-coverage-lines-utils';
import { PreFormData } from './types';
import { useApiProductsByCoverageLines } from './useApiProductsByCoverageLines';
import { formFieldsConfig, formValidationShape } from './utils';

interface PreFormProps {
  submission: PartialSubmission;
  afterSubmissionUpdated: ({
    oldCoverageLines,
    updatedCoverageLines,
  }: PostSubmissionCoverageLineUpdateProps) => Promise<void>;
  firstVisit: boolean;
  isBorFlow?: boolean;
}

export default function PreForm({ submission, afterSubmissionUpdated, firstVisit, isBorFlow }: PreFormProps) {
  const [isDirty, setIsDirty] = useState(false);
  const { submissionExtractedData } = submission;
  const { updateSubmission } = useMutateSubmission();
  const { reFetchWorkspace, updateSubmissionTasks } = useSubmissionsWorkspace();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isAcknowledgmentFlow } = useIsAcknowledgmentFlow();
  const { isBOROnboarding } = useIsBORFlow();

  const showProcessingBanner = submission.status === SubmissionStatus.Processing;
  const sendPromptReport = useSendPromptReport();

  const extractedCoverageLines = useMemo(() => {
    if (!submissionExtractedData?.extractedData?.coverageLines?.value) {
      return submissionExtractedData?.extractedData?.coverageLines;
    }

    const value = convertCoverageLinesTextToEnum(submissionExtractedData?.extractedData.coverageLines.value);

    if (value.length === 0) {
      return undefined;
    }

    return {
      ...submissionExtractedData?.extractedData?.coverageLines,
      value,
    };
  }, [submissionExtractedData?.extractedData?.coverageLines]);

  const preFormSuggestions: ExtractedDataFields | undefined = useMemo(() => {
    if (!extractedCoverageLines) {
      return undefined;
    }
    const coverageLinesSuggestionProps = extractValidCoverageLinesSuggestion(
      extractedCoverageLines,
      Object.values(CoverageLine),
    );
    if (!coverageLinesSuggestionProps) {
      return undefined;
    }
    return {
      [formFieldsConfig.coverageLines.name]: coverageLinesSuggestionProps,
    };
  }, [extractedCoverageLines]);

  const { methods, appliedSuggestions } = useFormProvider<PreFormData>({
    schema: formValidationShape(),
    setIsDirty,
    defaultValues: {
      coverageLines: submission ? submission.coverageLines : [],
    },
    suggestedValues: preFormSuggestions,
    applySuggestionsOnEmptyFields: firstVisit,
  });

  const coverageLinesFormValue = methods.watch(formFieldsConfig.coverageLines.name);

  const appliedCoverageLinesSuggestions = useMemo(
    () =>
      Array.isArray(appliedSuggestions[formFieldsConfig.coverageLines.name]?.value)
        ? {
            value: (appliedSuggestions[formFieldsConfig.coverageLines.name].value as CoverageLine[]).map(
              (appliedCoverageLine) => getCoverageLineConfig(appliedCoverageLine as CoverageLine).text,
            ),
            reason: <AIReason suggestion={appliedSuggestions[formFieldsConfig.coverageLines.name]} />,
          }
        : undefined,
    [appliedSuggestions],
  );

  const coverageLines = methods.watch('coverageLines');
  const { apiProductsByCoverageLines } = useApiProductsByCoverageLines(coverageLines);

  const onSubmit = () => {
    methods.handleSubmit(async (data: PreFormData) => {
      const oldCoverageLines = submission.coverageLines;
      const wasHeraldSubmission = getIsHeraldSubmission(submission);
      setIsSubmitting(true);
      try {
        await updateSubmission.mutateAsync({
          id: submission!.id,
          data: {
            coverageLines: isEmpty(data.coverageLines) ? undefined : data.coverageLines,
            draftAckSelectedProducts: [],
          },
        });

        if (submission.tasks.ReviewSubmission?.status === SubmissionTaskStatus.ToDo) {
          await updateSubmissionTasks({
            submissionId: submission!.id,
            status: SubmissionTaskStatus.InProgress,
            task: SubmissionTask.ReviewSubmission,
          });
        }

        await reFetchWorkspace();

        // Force React to flush the dirty flag update before we navigate away
        flushSync(() => {
          setIsDirty(false);
        });

        await afterSubmissionUpdated({
          oldCoverageLines,
          updatedCoverageLines: data.coverageLines,
          wasHeraldSubmission,
        });
        methods.reset(undefined, { keepValues: true });
        if (preFormSuggestions) {
          await sendPromptReport({
            formState: data,
            suggestedValueProps: preFormSuggestions,
            entityId: submission.id,
            submissionId: submission.id,
          });
        }
        setIsSubmitting(false);
      } catch {
        setIsSubmitting(false);
      }
    })();
  };

  const titleText = () => {
    if (isAcknowledgmentFlow) {
      return messages.acknowledgmentFlow.title;
    }
    return messages.editSubmission.coverageLines;
  };

  const beforeNavigation = async (transition: Transition) =>
    transition.location.pathname.includes(BROKER_NESTED_ROUTES.WORKSPACE.EDIT_SUBMISSION);

  return (
    <DirtyRouteGuard isDirty={isDirty} beforeNavigation={beforeNavigation}>
      <InnerPageLayout
        title={titleText()}
        className="edit-submission-pre-form"
        hideBackButton={isAcknowledgmentFlow || isBOROnboarding}
        headerTopContent={
          showProcessingBanner
            ? () => (
                <Banner
                  title={messages.acknowledgmentFlow.processingBanner.title}
                  subtitle={messages.acknowledgmentFlow.processingBanner.subTitle}
                  mode={BannerMode.Warning}
                />
              )
            : undefined
        }
      >
        {() => (
          <FormProvider {...methods}>
            <Box gap={2} maxWidth={640} margin="auto">
              {!isBorFlow && (
                <Box mb={5}>
                  <InfoBanner
                    sections={[
                      {
                        title: messages.acknowledgmentFlow.overviewBanner.title1,
                        content: messages.acknowledgmentFlow.overviewBanner.content1,
                      },
                      {
                        title: messages.acknowledgmentFlow.overviewBanner.title2,
                        content: messages.acknowledgmentFlow.overviewBanner.content2,
                      },
                    ]}
                  />
                </Box>
              )}

              <FormMultiSelectAutocomplete
                dropdownAfterTyping
                hideOptionalLabel
                freeSolo
                defaultValue={[]}
                {...formFieldsConfig.coverageLines}
                options={Object.keys(getCoverageLinesConfig(coverageLinesFormValue)).map((coverageLine) => ({
                  label: getCoverageLineConfig(coverageLine as CoverageLine).text,
                  value: coverageLine,
                }))}
                suggestion={appliedCoverageLinesSuggestions}
              />

              <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center">
                <Box>
                  {!isBorFlow && <ApiProductsDropDown menuPosition="left" products={apiProductsByCoverageLines} />}
                </Box>
                <Box minWidth={190}>
                  <Button fullWidth loading={isSubmitting} onClick={onSubmit} variant="contained">
                    {messages.buttons.start}
                  </Button>
                </Box>
              </Stack>
            </Box>
          </FormProvider>
        )}
      </InnerPageLayout>
    </DirtyRouteGuard>
  );
}
